import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FiX, FiClock, FiMapPin, FiUsers, FiMail } from 'react-icons/fi';

const API_BASE = process.env.REACT_APP_API_URL || '';
const getShopMediaSrc = (shopId, folder, fullUrl) => {
    if (!shopId || !fullUrl) return fullUrl || '';
    const filename = fullUrl.split('/').pop() || '';
    return `${API_BASE}/api/shops/${shopId}/media/${folder}/${encodeURIComponent(filename)}`;
};

const valueClass = 'text-blue-600';

const ViewShopModal = ({ shop, onClose }) => {
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const allMedia = [
        ...(shop.shopLogo || []).map((url) => ({ url, folder: 'logo' })),
        ...(shop.shopImage || []).map((url) => ({ url, folder: 'shop' })),
        ...(shop.shopPriceImage || []).map((url) => ({ url, folder: 'price' }))
    ];

    const goPrev = useCallback(() => {
        setLightboxIndex((i) => (i === null ? null : i <= 0 ? allMedia.length - 1 : i - 1));
    }, [allMedia.length]);
    const goNext = useCallback(() => {
        setLightboxIndex((i) => (i === null ? null : i >= allMedia.length - 1 ? 0 : i + 1));
    }, [allMedia.length]);

    useEffect(() => {
        if (lightboxIndex === null) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setLightboxIndex(null);
            if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
            if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxIndex, goPrev, goNext]);
    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        // Time is already in HH:mm format, just return it
        return timeString;
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800';
            case 'inactive':
                return 'bg-gray-100 text-gray-800';
            case 'closed':
                return 'bg-red-100 text-red-800';
            case 'premium':
                return 'bg-purple-100 text-purple-800';
            case 'feature':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        // Create date in Singapore timezone
        const date = new Date(dateString);
        return date.toLocaleDateString('en-SG', { 
            timeZone: 'Asia/Singapore',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const isExpired = (renewalDate) => {
        if (!renewalDate) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day
        const renewalDateObj = new Date(renewalDate);
        renewalDateObj.setHours(0, 0, 0, 0); // Set to start of day
        return renewalDateObj < today;
    };

    const contactList = Array.isArray(shop.shopContact) ? shop.shopContact : (shop.shopContact ? [shop.shopContact] : []);
    const whatsappList = Array.isArray(shop.shopWhatsapp) ? shop.shopWhatsapp : (shop.shopWhatsapp ? [shop.shopWhatsapp] : []);
    const telegramList = Array.isArray(shop.shopTelegram) ? shop.shopTelegram : (shop.shopTelegram ? [shop.shopTelegram] : []);
    const activeCount = shop.godMember ? shop.godMember.filter((m) => m.status === 'active').length : 0;
    const inactiveCount = shop.godMember ? shop.godMember.filter((m) => m.status !== 'active').length : 0;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
            <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Shop Details</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    {/* Shop Name (Region, Area) + badges + status, then Operating Hours */}
                    <div className="flex justify-between items-start flex-wrap gap-2">
                        <div>
                            <h4 className="text-xl font-semibold">
                                <span className={valueClass}>{shop.shopName}</span>
                                <span className="text-gray-600 font-normal"> ({shop.region}, {shop.area})</span>
                            </h4>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {shop.shopType && (
                                    <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">{shop.shopType}</span>
                                )}
                                {shop.shopCategory && (
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">{shop.shopCategory}</span>
                                )}
                                {shop.isHidden && (
                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs">Hidden</span>
                                )}
                            </div>
                            <p className="text-sm mt-1 text-gray-600">
                                <FiClock className="inline w-4 h-4 mr-1" />
                                Operating Hours: <span className={valueClass}>{formatTime(shop.openHour)} - {formatTime(shop.closeHour)}</span>
                            </p>
                            {(shop.massagePrice1hr != null && shop.massagePrice1hr !== '') && (
                                <p className="text-sm mt-1 text-gray-600">
                                    Massage Price (1hr): <span className={valueClass}>${Number(shop.massagePrice1hr)}</span>
                                </p>
                            )}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(shop.status)}`}>
                            {shop.status}
                        </span>
                    </div>

                    {/* Information Card: Contact, WhatsApp, Telegram, Website */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h5 className="font-medium flex items-center gap-2 mb-2">
                            <FiMail className="w-4 h-4 text-gray-400" />
                            Information
                        </h5>
                        <div className="space-y-1 text-sm">
                            {contactList.length > 0 && (
                                <p>
                                    Contact: {contactList.map((c, i) => (
                                        <span key={i}>
                                            {i > 0 && ', '}
                                            <a href={`tel:${c.replace(/\s/g, '')}`} className={`${valueClass} hover:underline`}>{c}</a>
                                        </span>
                                    ))}
                                </p>
                            )}
                            {whatsappList.length > 0 && (
                                <p>
                                    WhatsApp: {whatsappList.map((w, i) => (
                                        <span key={i}>
                                            {i > 0 && ', '}
                                            <a href={`https://wa.me/${w.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className={`${valueClass} hover:underline`}>{w}</a>
                                        </span>
                                    ))}
                                </p>
                            )}
                            {telegramList.length > 0 && (
                                <p>Telegram: <span className={valueClass}>{telegramList.filter(Boolean).join(', ')}</span></p>
                            )}
                            {shop.shopWebsite && (
                                <p>
                                    Website: <a href={shop.shopWebsite.startsWith('http') ? shop.shopWebsite : `https://${shop.shopWebsite}`} target="_blank" rel="noopener noreferrer" className={`${valueClass} hover:underline`}>{shop.shopWebsite}</a>
                                </p>
                            )}
                            {contactList.length === 0 && whatsappList.length === 0 && telegramList.length === 0 && !shop.shopWebsite && (
                                <p className="text-gray-500">No contact information</p>
                            )}
                        </div>
                    </div>

                    {/* Address row: full address, postal, region, area */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h5 className="font-medium flex items-center gap-2 mb-2">
                            <FiMapPin className="w-4 h-4 text-gray-400" />
                            Address
                        </h5>
                        <div className="space-y-1 text-sm">
                            <p><span className={valueClass}>{shop.address}</span></p>
                            <p>Postal: <span className={valueClass}>{shop.postal}</span></p>
                            <p>Region: <span className={valueClass}>{shop.region}</span>, Area: <span className={valueClass}>{shop.area}</span></p>
                        </div>
                    </div>

                    {/* God Member Card (Active: n, Inactive: n) */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h5 className="font-medium flex items-center gap-2 mb-2">
                            <FiUsers className="w-4 h-4 text-gray-400" />
                            God Members <span className="text-sm font-normal text-gray-600">(Active: {activeCount}, Inactive: {inactiveCount})</span>
                        </h5>
                        <div className="mt-2">
                            {shop.godMember && shop.godMember.length > 0 ? (
                                <div className="space-y-2">
                                    {shop.godMember.map((member) => (
                                        <div key={member._id} className="flex items-center space-x-2 flex-wrap">
                                            <span className={valueClass}>{member.fullName}</span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(member.status)}`}>
                                                {member.status}
                                            </span>
                                            <span className={`text-xs ${isExpired(member.renewalDate) ? 'text-red-600' : 'text-gray-500'}`}>
                                                (Renewal: {formatDate(member.renewalDate)})
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 text-sm">No members assigned</p>
                            )}
                        </div>
                    </div>

                    {/* Logo / Image card - click for lightbox, keyboard left/right */}
                    {allMedia.length > 0 && (
                        <div className="border border-gray-200 rounded-lg p-4">
                            <h5 className="font-medium mb-2">Logo & Images</h5>
                            <div className="flex flex-wrap gap-3">
                                {allMedia.map((item, index) => (
                                    <img
                                        key={`${item.folder}-${item.url}`}
                                        src={getShopMediaSrc(shop._id, item.folder, item.url)}
                                        alt={item.folder === 'logo' ? 'Shop logo' : 'Shop'}
                                        className="w-24 h-24 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-90"
                                        onClick={() => setLightboxIndex(index)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Lightbox */}
                    {lightboxIndex !== null && allMedia[lightboxIndex] && (
                        <div
                            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
                            onClick={() => setLightboxIndex(null)}
                        >
                            <button type="button" className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 rounded p-2" onClick={(e) => { e.stopPropagation(); goPrev(); }} aria-label="Previous">
                                ‹
                            </button>
                            <img
                                src={getShopMediaSrc(shop._id, allMedia[lightboxIndex].folder, allMedia[lightboxIndex].url)}
                                alt=""
                                className="max-w-[90vw] max-h-[90vh] object-contain"
                                onClick={(e) => e.stopPropagation()}
                            />
                            <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 rounded p-2" onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label="Next">
                                ›
                            </button>
                            <button type="button" className="absolute top-4 right-4 text-white hover:bg-white/20 rounded p-2" onClick={() => setLightboxIndex(null)} aria-label="Close">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>
                    )}

                    {/* Remarks card - only if has remarks */}
                    {shop.remarks && shop.remarks.trim() && (
                        <div className="border border-gray-200 rounded-lg p-4">
                            <h5 className="font-medium mb-2">Remarks</h5>
                            <p className={`text-sm whitespace-pre-wrap ${valueClass}`}>{shop.remarks}</p>
                        </div>
                    )}
                </div>

                <div className="mt-6">
                    <button onClick={onClose} className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

ViewShopModal.propTypes = {
    shop: PropTypes.shape({
        shopName: PropTypes.string.isRequired,
        address: PropTypes.string.isRequired,
        postal: PropTypes.string.isRequired,
        region: PropTypes.string.isRequired,
        area: PropTypes.string.isRequired,
        openHour: PropTypes.string,
        closeHour: PropTypes.string,
        massagePrice1hr: PropTypes.number,
        status: PropTypes.string.isRequired,
        shopType: PropTypes.string,
        shopContact: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        shopWhatsapp: PropTypes.arrayOf(PropTypes.string),
        shopTelegram: PropTypes.arrayOf(PropTypes.string),
        shopCategory: PropTypes.string,
        shopWebsite: PropTypes.string,
        isHidden: PropTypes.bool,
        shopLogo: PropTypes.arrayOf(PropTypes.string),
        shopImage: PropTypes.arrayOf(PropTypes.string),
        shopPriceImage: PropTypes.arrayOf(PropTypes.string),
        remarks: PropTypes.string,
        godMember: PropTypes.arrayOf(PropTypes.shape({
            _id: PropTypes.string.isRequired,
            fullName: PropTypes.string.isRequired
        })),
        createdAt: PropTypes.string.isRequired,
        updatedAt: PropTypes.string.isRequired
    }).isRequired,
    onClose: PropTypes.func.isRequired
};

export default ViewShopModal; 